import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { MYACCOUNT_APPID } from '@rci/self-serve-fido/app.constant';
import { LoginStateFacade } from '@rci/self-serve-fido/core/fido-login-state/login-state.facade';
import { StateService } from '@rci/state-service';
import { UserMonitoringService } from '@rci/user-monitoring-service';
import { AccountDetailFacadeService, AccountListFacadeService } from '@rogers/self-serve-common/account';
import { BillingDetailFacadeService } from '@rogers/self-serve-common/billing-detail';
import { BrtSwitchFacadeService } from '@rogers/self-serve-common/brt-switch';
import { UserPermissionFacadeService } from '@rogers/self-serve-common/user-permission';
import { ClearTargetedOffers } from '@rogers/self-serve-targeted-offers';
import { getLatestValue } from '@rogers/self-serve-utils';
import { combineLatest, Subject } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import * as globalActions from './app.actions';
import { SelfServeAppState } from './app.state.model';
import { loadAuthState, updateAuthState } from './auth/auth.actions';
import { loadUserInformation, resetUserInformation } from './user-information';
import { UserInformationStateFacade } from './user-information/user-information-state.facade';
import { AlertFacadeService } from '@rogers/self-serve-common/alert';
import { AppSettingFacadeService } from '@rogers/self-serve-common/app-setting';

@Injectable()
export class AppFacade {
  isUserAuthenticated = false;

  allSubscriptionsForPageDisplay$ = this.userInformationStateFacade.selectedAccountNumber$.pipe(
    switchMap(accountNumber => this.accountDetailFacadeService.getSubscriptionsForPageDisplay$(accountNumber)),
  );

  private permissionsLoaded = false;
  private readonly _clearStore$ = new Subject();

  constructor(
    private readonly accountDetailFacadeService: AccountDetailFacadeService,
    private readonly accountListFacadeService: AccountListFacadeService,
    private readonly alertFacadeService: AlertFacadeService,
    private readonly appSettingFacadeService: AppSettingFacadeService,
    private readonly billingDetailFacadeService: BillingDetailFacadeService,
    private readonly brtSwitchFacadeService: BrtSwitchFacadeService,
    private readonly loginStateFacade: LoginStateFacade,
    private readonly stateService: StateService,
    private readonly store: Store<SelfServeAppState>,
    private readonly userMonitoringService: UserMonitoringService,
    private readonly userInformationStateFacade: UserInformationStateFacade,
    private readonly userPermissionFacadeService: UserPermissionFacadeService,
  ) {}

  get clearStore$() {
    return this._clearStore$.asObservable();
  }

  get language() {
    return getLatestValue<string>(this.stateService.language$);
  }

  get province() {
    return getLatestValue<string>(this.stateService.province$);
  }

  initializeStore() {
    this.store.dispatch(globalActions.setAppId({ appId: MYACCOUNT_APPID }));
    this.store.dispatch(loadUserInformation());
    this.store.dispatch(loadAuthState());
    let fetchAccountList = true;

    combineLatest([this.loginStateFacade.isLoggedIn$, this.stateService.language$])
      .pipe(
        tap(([loggedIn]) => {
          this.appSettingFacadeService.loadSettingsIfNotLoaded('self-serve');
          if (!loggedIn) {
            if (this.isUserAuthenticated) {
              this.isUserAuthenticated = false;
              this.permissionsLoaded = false;
              this.resetStore();
            }
            return;
          }
          this.userMonitoringService.identifyUser(this.userInformationStateFacade.userName);
          this.isUserAuthenticated = true;
          this.loadPermissions();
          if (fetchAccountList) {
            this.accountListFacadeService.fetchAccountList();
            fetchAccountList = false;
          }
        }),
      )
      .subscribe();
    return new Promise<boolean>((resolve, _reject) => {
      this.stateService.loginState$.pipe(filter(loginState => !!loginState)).subscribe(_loginState => {
        resolve(true);
      });
    });
  }

  resetStore(guid: string = '') {
    // reset global states, feature states should be cleared within the respective feature modules
    this.store.dispatch(new ClearTargetedOffers());
    this.accountDetailFacadeService.clearAccountDetails();
    this.accountListFacadeService.resetAccountList();
    this.alertFacadeService.clearAlerts();
    this.billingDetailFacadeService.clearBillingDetails();
    this.store.dispatch(updateAuthState({ guid }));
    this.store.dispatch(resetUserInformation());
    this.stateService.updateLoginState();
    this.brtSwitchFacadeService.resetBrtSwitch();
    this.userPermissionFacadeService.resetUserPermissions();
    this._clearStore$.next(undefined);
  }

  private loadPermissions() {
    if (!this.permissionsLoaded) {
      this.permissionsLoaded = true;
      this.userPermissionFacadeService.loadUserPermissions();
      this.brtSwitchFacadeService.getBrtSwitch();
    }
  }
}
